import { NgModule } from '@angular/core';
import { ExtraOptions, RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './common/guard/auth-guard/auth.guard';
import { OrganizationGuard } from './common/guard/organization-guard/organization.guard';
import { ReverseGuard } from './common/guard/reverse-guard/reverse.guard';
import { SysAdminGuard } from './common/guard/sysAdmin/sys-admin.guard';
import { UnsavedChangesGuard } from './common/guard/unsavedChanges/unsaved-changes.guard';
import { HelpDetailsComponent } from './common/modules/support/components/help-details/help-details.component';
import { HelpComponent } from './common/modules/support/components/help/help.component';
import { AppLayoutComponent } from './layout/components/layout/app.layout.component';

const routerOptions: ExtraOptions = {
  anchorScrolling: 'enabled',
  canceledNavigationResolution: 'computed',
  onSameUrlNavigation: 'reload',
  scrollPositionRestoration: 'top',
  useHash: false
};

const routes: Routes = [
  {
    path: '',
    redirectTo: 'app',
    pathMatch: 'full'
  },
  {
    path: 'auth',
    data: { breadcrumb: 'Auth' },
    loadChildren: () =>
      import('./common/modules/auth/auth.module').then((m) => m.AuthModule),
    canActivate: [ReverseGuard]
  },
  {
    path: 'verify-account',
    data: { breadcrumb: 'Verify Account' },
    loadComponent: () =>
      import(
        './common/modules/auth/components/verify-account/verify-account.component'
      ).then((c) => c.VerifyAccountComponent)
  },
  {
    path: 'verify-account/:inviteId',
    data: { breadcrumb: 'Verify Account' },
    loadComponent: () =>
      import(
        './common/modules/auth/components/verify-account/verify-account.component'
      ).then((c) => c.VerifyAccountComponent)
  },
  {
    path: 'organization',
    data: { breadcrumb: 'Organization' },
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./common/modules/organization/organization.module').then(
        (m) => m.OrganizationModule
      )
  },
  {
    path: 'support',
    data: { breadcrumb: 'Support' },
    loadChildren: () =>
      import('./common/modules/support/support.module').then((m) => m.SupportModule)
  },
  {
    path: 'storefront',
    data: { breadcrumb: 'Storefront' },
    loadChildren: () =>
      import('./EBMSuite/modules/site-builder/site-builder.module').then(
        (m) => m.SiteBuilderModule
      )
  },
  {
    path: 'lock',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./EBMSuite/modules/lockscreen/lockscreen.module').then(
        (m) => m.LockScreenModule
      )
  },
  {
    path: 'app',
    component: AppLayoutComponent,
    canActivate: [AuthGuard, OrganizationGuard],
    children: [
      {
        path: '',
        data: { breadcrumb: 'Dashboard' },
        loadChildren: () =>
          import('./EBMSuite/modules/dashboard/dashboard.module').then(
            (m) => m.DashboardModule
          )
      },
      {
        path: 'user',
        data: { breadcrumb: 'Users' },
        loadChildren: () =>
          import('./common/modules/user/user.module').then((m) => m.UserModule)
      },
      {
        path: 'users/list',
        data: { breadcrumb: 'Users List' },
        loadComponent: () =>
          import(
            './common/modules/organization/components/members/members.component'
          ).then((c) => c.MembersComponent)
      },
      {
        path: 'support',
        data: { breadcrumb: 'Support' },
        loadChildren: () =>
          import('./common/modules/support/support.module').then((m) => m.SupportModule)
      },

      {
        path: 'folder',
        data: {
          breadcrumb: 'Board Packs'
        },
        children: [
          {
            path: 'list',
            data: { breadcrumb: 'List' },
            loadComponent: () =>
              import('./common/modules/document/folder-list/folder-list.component').then(
                (c) => c.FolderListComponent
              )
          },
          {
            path: 'files/:moduleType/:moduleItemId/:moduleName',
            data: { breadcrumb: 'Files' },
            loadComponent: () =>
              import(
                './common/modules/document/folder-details/folder-details.component'
              ).then((c) => c.FolderDetailsComponent)
          }
        ]
      },
      {
        path: 'documents/viewDocument/:id',
        data: { breadcrumb: 'Document Details' },
        canDeactivate: [UnsavedChangesGuard],
        loadComponent: () =>
          import('./common/modules/document/views/pdf-viewer.component').then(
            (c) => c.DocumentViewerComponent
          )
      },
      {
        path: 'documents',
        data: { breadcrumb: 'Documents' },
        loadComponent: () =>
          import('./common/modules/document/views/pdf-viewer.component').then(
            (c) => c.DocumentViewerComponent
          )
      },
      {
        path: 'documents/viewDocument/:id',
        data: { breadcrumb: 'Document Details' },
        canDeactivate: [UnsavedChangesGuard],
        loadComponent: () =>
          import('./common/modules/document/views/pdf-viewer.component').then(
            (c) => c.DocumentViewerComponent
          )
      },
      {
        path: 'organization',
        data: { breadcrumb: 'Organizations' },
        loadChildren: () =>
          import('./common/modules/organization/organization.module').then(
            (m) => m.OrganizationModule
          )
      },
      {
        path: 'notifications',
        data: { breadcrumb: 'Notifications' },
        loadChildren: () =>
          import('./common/modules/notifications/notifications.module').then(
            (m) => m.NotificationsModule
          )
      },
      {
        path: 'comments',
        data: { breadcrumb: 'Comments' },
        loadComponent: () =>
          import('./common/modules/annotation/annotation-list.component').then(
            (m) => m.AnnotationListComponent
          )
      },
      {
        path: 'feedback',
        data: { breadcrumb: 'Feedback' },
        loadChildren: () =>
          import('./common/modules/feedback/feedback.module').then(
            (m) => m.FeedbackModule
          )
      },
      {
        path: 'faq',
        data: { breadcrumb: 'Frequently Asked Questions' },
        loadChildren: () =>
          import('./common/modules/support/components/faqs/faqs.module').then(
            (m) => m.FAQsModule
          )
      },
      {
        path: 'group',
        children: [
          {
            path: 'group-list',
            data: { breadcrumb: 'Groups List' },
            loadComponent: () =>
              import('./common/modules/group/group-list/group-list.component').then(
                (m) => m.GroupListComponent
              )
          },
          {
            path: 'group-details/:groupId',
            data: { breadcrumb: 'Group Details' },
            loadComponent: () =>
              import('./common/modules/group/group-details/group-details.component').then(
                (m) => m.GroupDetailsComponent
              )
          },
          {
            path: 'group-create',
            data: { breadcrumb: 'Create Group' },
            loadComponent: () =>
              import('./common/modules/group/group-create/group-create.component').then(
                (m) => m.GroupCreateComponent
              )
          },
          {
            path: 'group-edit/:groupId',
            data: { breadcrumb: 'Edit Group' },
            loadComponent: () =>
              import('./common/modules/group/group-create/group-create.component').then(
                (m) => m.GroupCreateComponent
              )
          }
        ]
      },

      //******************************EBM Suite*****************************************
      {
        path: 'sales',
        data: { breadcrumb: 'Sales' },
        loadChildren: () =>
          import('./EBMSuite/modules/sales/sales.module').then((m) => m.SalesModule)
      },
      {
        path: 'invoice',
        data: { breadcrumb: 'Invoices' },
        loadChildren: () =>
          import('./EBMSuite/modules/invoice/invoice.module').then((m) => m.InvoiceModule)
      },
      {
        path: 'quotes',
        data: { breadcrumb: 'Quotes' },
        loadChildren: () =>
          import('./EBMSuite/modules/quotes/quotes.module').then((m) => m.QuotesModule)
      },
      {
        path: 'orders',
        data: { breadcrumb: 'Orders' },
        loadChildren: () =>
          import('./EBMSuite/modules/orders/orders.module').then((m) => m.OrdersModule)
      },
      {
        path: 'contacts',
        data: { breadcrumb: 'Contacts' },
        loadChildren: () =>
          import('./common/modules/contacts/contacts.module').then(
            (m) => m.ContactsModule
          )
      },
      {
        path: 'items',
        data: { breadcrumb: 'Items' },

        loadChildren: () =>
          import('./EBMSuite/modules/items/items.module').then((m) => m.ItemsModule)
      },
      {
        path: 'expense',
        data: { breadcrumb: 'Expenses' },
        loadChildren: () =>
          import('./EBMSuite/modules/expenses/expense.module').then(
            (m) => m.ExpenseModule
          )
      },
      {
        path: 'asset',
        data: { breadcrumb: 'Asset' },
        loadChildren: () =>
          import('./EBMSuite/modules/asset/asset.module').then((m) => m.AssetModule)
      },
      {
        path: 'settings',
        data: { breadcrumb: 'Organization Settings' },
        loadChildren: () =>
          import('./EBMSuite/modules/settings/settings.module').then(
            (m) => m.SettingsModule
          )
      },
      {
        path: 'features',
        data: { breadcrumb: 'Features' },
        loadChildren: () =>
          import('./EBMSuite/modules/admin/features/features.module').then(
            (m) => m.FeaturesModule
          )
      },
      {
        path: 'help',
        data: { breadcrumb: 'Help' },
        component: HelpComponent
      },
      {
        path: 'help-details/:helpId',
        data: { breadcrumb: 'Help' },
        component: HelpDetailsComponent
      },
      {
        path: 'documentation',
        data: { breadcrumb: 'Documentation' },
        loadChildren: () =>
          import('./EBMSuite/modules/documentation/documentation.module').then(
            (m) => m.DocumentationModule
          ),
        canActivate: [AuthGuard]
      },
      {
        path: 'subscription',
        data: { breadcrumb: 'Subscription' },
        loadComponent: () =>
          import(
            './common/modules/license/components/license-list/license-list.component'
          ).then((component) => component.LicenseListComponent)
      },
      {
        path: 'subscription/invoice',
        data: { breadcrumb: 'Subscription Invoice' },
        loadComponent: () =>
          import(
            './common/modules/license/components/license-invoice/license-invoice.component'
          ).then((mod) => mod.LicenseInvoiceComponent)
      },

      // ADMIN ROUTES
      {
        path: 'admin',
        canActivate: [SysAdminGuard, AuthGuard],
        loadChildren: () =>
          import('./EBMSuite/modules/admin/admin.module').then((m) => m.AdminModule)
      },
      {
        path: 'features',
        loadChildren: () =>
          import('./EBMSuite/modules/admin/features/features.module').then(
            (m) => m.FeaturesModule
          )
      },
      {
        path: 'license-item/create',
        canActivate: [SysAdminGuard, AuthGuard],
        loadComponent: () =>
          import(
            './common/modules/license/components/license-item-create/license-item-create.component'
          ).then((mod) => mod.LicenseItemCreateComponent)
      },
      {
        path: 'license-item/create/:licenseItemId',
        canActivate: [SysAdminGuard, AuthGuard],
        loadComponent: () =>
          import(
            './common/modules/license/components/license-item-create/license-item-create.component'
          ).then((mod) => mod.LicenseItemCreateComponent)
      },
      {
        path: 'license-item',
        canActivate: [SysAdminGuard, AuthGuard],
        loadComponent: () =>
          import(
            './common/modules/license/components/license-item-list/license-item-list.component'
          ).then((mod) => mod.LicenseItemListComponent)
      },
      {
        path: 'license-package/create',
        canActivate: [SysAdminGuard, AuthGuard],
        loadComponent: () =>
          import(
            './common/modules/license/components/license-package-create/license-package-create.component'
          ).then((mod) => mod.LicensePackageCreateComponent)
      },
      {
        path: 'license-package/create/:licensePackageId',
        canActivate: [SysAdminGuard, AuthGuard],
        loadComponent: () =>
          import(
            './common/modules/license/components/license-package-create/license-package-create.component'
          ).then((mod) => mod.LicensePackageCreateComponent)
      },
      {
        path: 'license-package',
        canActivate: [SysAdminGuard, AuthGuard],
        loadComponent: () =>
          import(
            './common/modules/license/components/license-package/license-package.component'
          ).then((mod) => mod.LicensePackageComponent)
      },

      // ***********************SIBASI HR**************************

      {
        path: 'employees',
        data: { breadcrumb: 'Employee' },
        loadChildren: () =>
          import('./EBMSuite_HR/modules/employee/employee.module').then(
            (m) => m.EmployeeModule
          )
      },
      {
        path: 'leave',
        data: { breadcrumb: 'Leave' },
        loadChildren: () =>
          import('./EBMSuite_HR/modules/leave/leave.module').then((m) => m.LeaveModule)
      },
      {
        path: 'attendance',
        data: { breadcrumb: 'Attendance' },
        loadChildren: () =>
          import('./EBMSuite_HR/modules/attendance/attendance.module').then(
            (m) => m.AttendanceModule
          )
      },
      {
        path: 'location',
        children: [
          {
            path: 'location-list',
            data: { breadcrumb: 'Locations List' },
            loadComponent: () =>
              import(
                './EBMSuite_HR/modules/location/location-list/location-list.component'
              ).then((m) => m.LocationListComponent)
          },
          {
            path: 'location-details/:locationId',
            data: { breadcrumb: 'Location Details' },
            loadComponent: () =>
              import(
                './EBMSuite_HR/modules/location/location-details/location-details.component'
              ).then((m) => m.LocationDetailsComponent)
          },
          {
            path: 'location-create',
            data: { breadcrumb: 'Create Location' },
            loadComponent: () =>
              import(
                './EBMSuite_HR/modules/location/location-create/location-create.component'
              ).then((m) => m.LocationCreateComponent)
          },
          {
            path: 'location-edit/:locationId',
            data: { breadcrumb: 'Edit Location' },
            loadComponent: () =>
              import(
                './EBMSuite_HR/modules/location/location-create/location-create.component'
              ).then((m) => m.LocationCreateComponent)
          }
        ]
      }
    ]
  },
  {
    path: 'notfound',
    data: { breadcrumb: 'Not Found' },
    loadComponent: () =>
      import('./common/modules/utils/components/notfound/notfound.component').then(
        (m) => m.NotfoundComponent
      )
  },
  { path: '**', redirectTo: '/notfound' }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, routerOptions)],
  exports: [RouterModule]
})
export class AppRoutingModule {}
